<template>
  <Teleport to='#modal'>
    <div
      class="modal d-block centered base-modal-overlay"
      :class="{ 'inga-modal-overlay-dark': isDarkMode }"
      tabindex="1"
      role="dialog"
      ref="baseModalRef"
      @keyup.esc="onEsc"
    >
      <div 
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable" 
        :style="dialogStyle"
      >
        <div
          class="modal-content border-0"
          :class="{ 'inga-modal-panel-dark': isDarkMode }"
        >
          <div class="modal-header mx-2 d-flex justify-content-between align-items-center border-bottom-0">
            <div class="d-flex align-items-center gap-3 min-w-0">
              <div
                v-if="isinTitle"
                class="d-flex align-items-center justify-content-center fw-bold"
                :style="showIsinLogo
                  ? { width: '40px', height: '40px', flexShrink: 0 }
                  : {
                      width: '40px',
                      height: '40px',
                      flexShrink: 0,
                      borderRadius: '50%',
                      backgroundColor: isDarkMode ? 'rgba(148,163,184,0.22)' : 'rgba(100,116,139,0.12)',
                      color: isinTitle.Color || '#64748b',
                      fontSize: '0.85rem'
                    }"
              >
                <img
                  v-if="showIsinLogo"
                  :src="shareLogoUrl(isinTitle.ISIN)"
                  :alt="isinTitle.Name"
                  style="width: 40px; height: 40px; object-fit: contain;"
                  @error="logoFailed = true"
                />
                <span v-else>{{ shareMonogram(isinTitle.Name) }}</span>
              </div>
              <h4
                v-if="stackedTitle"
                class="modal-title-stack mb-0"
                :class="{ 'text-slate-100': isDarkMode }"
              >
                <span class="modal-title-main">{{ stackedTitle.main }}</span>
                <span class="modal-title-sub">{{ stackedTitle.sub }}</span>
              </h4>
              <h4
                v-else
                class="font-weight-bolder mb-0 text-truncate"
                :class="{ 'text-slate-100': isDarkMode }"
              >{{ store.state.modal.title }}</h4>
            </div>
            <span
              class="inga-close-btn"
              :class="{ 'inga-close-btn-dark': isDarkMode }"
              role="button"
              tabindex="0"
              aria-label="Zavřít"
              @click="store.dispatch('closeModal')"
              @keydown.enter.prevent="store.dispatch('closeModal')"
              @keydown.space.prevent="store.dispatch('closeModal')"
            >&times;</span>
          </div>
          <div class="modal-body p-2">
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { shareLogoUrl, shareMonogram } from "@/helpers/shareLogos";

const baseModalRef = ref(null);
const store = useStore();
const logoFailed = ref(false);
const isDarkMode = computed(() => !!store.state.isDarkMode);
const dialogStyle = computed(() => {
  if (store.state.modal.view === "ISINChart") return "max-width: 900px";
  if (store.state.modal.view === "CropperImage") return "max-width: 480px";
  if (store.state.modal.view === "CancelAccount") return "max-width: 600px";
  if (store.state.modal.view === "TopUpCredit") return "max-width: 460px";
  if (store.state.modal.view === "WithdrawCredit") return "max-width: 460px";
  return "";
});

const stackedTitle = computed(() => {
  const view = store.state.modal.view;
  if (view !== "TopUpCredit" && view !== "WithdrawCredit") return null;
  const parts = String(store.state.modal.title || "").trim().split(/\s+/);
  if (parts.length < 2) return null;
  return { main: parts[0], sub: parts.slice(1).join(" ") };
});

const isinTitle = computed(() => {
  if (store.state.modal.view !== "ISINChart") return null;
  const data = store.state.ISINChartData;
  return data?.Data?.Title || data?.Title || null;
});

const showIsinLogo = computed(() => {
  const isin = isinTitle.value?.ISIN;
  return !!(isin && shareLogoUrl(isin) && !logoFailed.value);
});

const onEsc = () => {
  if (store.state.lightbox?.isOpen) return;
  store.dispatch("closeModal");
};

onMounted(() => {
  baseModalRef.value.focus()
});

watch(() => isinTitle.value?.ISIN, () => {
  logoFailed.value = false;
});
</script>

<style scoped>
.base-modal-overlay {
  z-index: 2200 !important;
}

.inga-modal-overlay-dark {
  background: rgba(2, 6, 23, 0.78) !important;
  backdrop-filter: blur(6px);
}

.inga-modal-panel-dark {
  background: #334155 !important;
  color: #E2E8F0 !important;
  border: 1px solid #94A3B8 !important;
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 138, 76, 0.2) !important;
}

.inga-modal-panel-dark h4,
.text-slate-100 {
  color: #FFFFFF !important;
}

.modal-title-stack {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.modal-title-main {
  font-weight: 800;
  font-size: 1.15rem;
}

.modal-title-sub {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: lowercase;
  opacity: 0.92;
}

.inga-close-btn {
  -webkit-appearance: none !important;
  appearance: none !important;
  background: transparent !important;
  background-image: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: #94a3b8 !important;
  font-size: 1.75rem !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  padding: 0 4px !important;
  min-width: 0 !important;
  width: auto !important;
  height: auto !important;
}
</style>
